import { type ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';

type StatusConfig = {
  label?: string;
  iconName?: IconName;
  background: string;
  color: string;
};

type GenericColorVariant =
  | 'blue'
  | 'indigo'
  | 'eggplant'
  | 'rose'
  | 'red'
  | 'tangerine'
  | 'yellow'
  | 'green'
  | 'seaweed'
  | 'neutral1'
  | 'neutral2'
  | 'disabled';

type AppointmentStatusVariants =
  | 'appointment-confirmed'
  | 'appointment-unconfirmed'
  | 'appointment-attempted'
  | 'appointment-in-office'
  | 'appointment-completed'
  | 'appointment-no-show'
  | 'appointment-canceled'
  | 'appointment-unknown';

type AppointmentRequestSourceVariants = 'booked-online' | 'staff-booked' | 'ai-booked' | 'direct-booked';

type RequestStatusVariants =
  | 'request-pending'
  | 'request-accepted'
  | 'request-rejected'
  | 'contacted'
  | 'request-dismissed'
  | 'request-done'
  | 'request-deleted'
  | 'request-unknown';

type BookingRulesVariants =
  | 'direct-booking'
  | 'request-only-booking'
  | 'staff-only-booking'
  | 'online-scheduling'
  | 'ai-receptionist'
  | 'staff-only';

export type StatusBadgeVariants =
  | 'ai-requested'
  | 'ai-added'
  | 'online-requested'
  | 'urgent'
  | AppointmentRequestSourceVariants
  | GenericColorVariant
  | AppointmentStatusVariants
  | RequestStatusVariants
  | BookingRulesVariants;

type Props = {
  icon?: IconName;
  variant?: StatusBadgeVariants;
  children?: ReactNode; // used as label for generic color variants
  iconOnly?: boolean;
  disabled?: boolean;
  className?: string;
};

export const StatusBadge = ({ icon, variant, children, iconOnly = false, disabled = false, className }: Props) => {
  const { t } = useTranslation('schedule');

  const { label, iconName, background, color }: StatusConfig = useMemo(() => {
    switch (variant) {
      case 'ai-requested':
        return {
          label: t('AI-Requested'),
          iconName: 'aiassistant-small',
          background: 'linear-gradient(100deg, var(--fabric-color-blue-10), var(--fabric-color-seaweed-5))',
          color: 'var(--fabric-color-black)',
        };
      case 'ai-added':
        return {
          label: t('AI-Added'),
          iconName: 'aiassistant-small',
          background: 'linear-gradient(100deg, var(--fabric-color-blue-10), var(--fabric-color-seaweed-5))',
          color: 'var(--fabric-color-black)',
        };
      case 'online-requested':
        return {
          label: t('Online Requested'),
          background: 'var(--fabric-color-indigo-alpha-50-alpha-25)',
          color: 'var(--fabric-color-indigo-70)',
        };
      case 'urgent':
        return {
          label: t('Urgent'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
          iconName: 'alert',
        };

      // Request Statuses
      case 'request-pending':
        return {
          label: t('Request Pending'),
          background: 'var(--fabric-color-tangerine-alpha-50-alpha-25)',
          color: 'var(--fabric-color-tangerine-70)',
        };
      case 'request-accepted':
        return {
          label: t('Request Accepted'),
          background: 'var(--fabric-color-green-alpha-50-alpha-25)',
          color: 'var(--fabric-color-green-70)',
        };
      case 'request-rejected':
        return {
          label: t('Request Rejected'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'contacted':
        return {
          label: t('Contacted'),
          background: 'var(--fabric-color-indigo-alpha-50-alpha-25)',
          color: 'var(--fabric-color-indigo-70)',
        };
      case 'request-dismissed':
        return {
          label: t('Dismissed'),
          background: 'var(--fabric-color-neutral-alpha-100-alpha-10)',
          color: 'var(--fabric-color-neutral-alpha-100-alpha-60)',
        };
      case 'request-done':
        return {
          label: t('Done'),
          background: 'var(--fabric-color-neutral-alpha-100-alpha-20)',
          color: 'var(--fabric-color-neutral-alpha-100-alpha-75)',
        };
      case 'request-deleted':
        return {
          label: t('Deleted'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'request-unknown':
        return {
          label: t('Other'),
          background: 'var(--fabric-color-eggplant-alpha-50-alpha-25)',
          color: 'var(--fabric-color-eggplant-70)',
        };

      // Appointment Request Sources
      case 'staff-booked':
        return {
          label: t('Booked by Staff'),
          background: 'var(--fabric-color-blue-alpha-50-alpha-25)',
          color: 'var(--fabric-color-blue-70)',
          iconName: 'user',
        };
      case 'booked-online':
        return {
          label: t('Booked Online'),
          background: 'var(--fabric-color-eggplant-alpha-50-alpha-25)',
          color: 'var(--fabric-color-eggplant-70)',
          iconName: 'cell-phone-small',
        };
      case 'ai-booked':
        return {
          label: t('AI-Booked'),
          background: `linear-gradient(100deg, var(--fabric-color-seaweed-5), var(--fabric-color-accent-blue-subtle))`,
          color: 'var(--fabric-color-text-primary)',
          iconName: 'aiassistant-small',
        };
      case 'direct-booked':
        return {
          label: t('Direct Booked'),
          background: 'var(--fabric-color-green-alpha-50-alpha-25)',
          color: 'var(--fabric-color-green-70)',
          iconName: 'lists',
        };

      // Appointment Statuses
      case 'appointment-unconfirmed':
        return {
          label: t('Appointment Unconfirmed'),
          background: 'var(--fabric-color-yellow-alpha-20-alpha-25)',
          color: 'var(--fabric-color-yellow-70)',
        };
      case 'appointment-confirmed':
        return {
          label: t('Appointment Confirmed'),
          background: 'var(--fabric-color-blue-alpha-50-alpha-25)',
          color: 'var(--fabric-color-blue-70)',
        };
      case 'appointment-attempted':
        return {
          label: t('Attempted contact'),
          background: 'var(--fabric-color-indigo-alpha-50-alpha-25)',
          color: 'var(--fabric-color-indigo-70)',
        };
      case 'appointment-in-office':
        return {
          label: t('In-Office'),
          background: 'var(--fabric-color-seaweed-alpha-50-alpha-25)',
          color: 'var(--fabric-color-seaweed-70)',
        };
      case 'appointment-completed':
        return {
          label: t('Completed'),
          background: 'var(--fabric-color-green-alpha-50-alpha-25)',
          color: 'var(--fabric-color-green-70)',
        };
      case 'appointment-no-show':
        return {
          label: t('No-show'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'appointment-canceled':
        return {
          label: t('Cancelled'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'appointment-unknown':
        return {
          label: t('Unknown'),
          background: 'var(--fabric-color-neutral-alpha-100-alpha-10)',
          color: 'var(--fabric-color-neutral-alpha-100-alpha-20)',
        };

      // Booking Rules
      case 'direct-booking':
        return {
          label: t('Self Bookable'),
          background: 'var(--fabric-color-green-alpha-50-alpha-25)',
          color: 'var(--fabric-color-green-70)',
          iconName: 'check',
        };
      case 'request-only-booking':
        return {
          label: t('Request Only'),
          background: 'var(--fabric-color-indigo-alpha-50-alpha-25)',
          color: 'var(--fabric-color-indigo-70)',
          iconName: 'alert',
        };
      case 'staff-only-booking':
        return {
          label: t('Staff Only'),
          background: 'var(--fabric-color-neutral-alpha-100-alpha-10)',
          color: 'var(--fabric-color-neutral-70)',
        };
      case 'online-scheduling':
        return {
          label: t('Online Scheduling'),
          background: 'var(--fabric-color-eggplant-alpha-50-alpha-25)',
          color: 'var(--fabric-color-eggplant-70)',
          iconName: 'cell-phone-small',
        };
      case 'ai-receptionist':
        return {
          label: t('AI Receptionist'),
          background: `linear-gradient(100deg, var(--fabric-color-seaweed-5), var(--fabric-color-accent-blue-subtle))`,
          color: 'var(--fabric-color-text-primary)',
          iconName: 'aiassistant-small',
        };
      case 'staff-only':
        return {
          label: t('Staff'),
          background: 'var(--fabric-color-blue-alpha-50-alpha-25)',
          color: 'var(--fabric-color-blue-70)',
          iconName: 'user',
        };

      // Generic color variants
      case 'blue':
        return {
          background: 'var(--fabric-color-blue-alpha-50-alpha-25)',
          color: 'var(--fabric-color-blue-70)',
        };
      case 'indigo':
        return {
          background: 'var(--fabric-color-indigo-alpha-50-alpha-25)',
          color: 'var(--fabric-color-indigo-70)',
        };
      case 'eggplant':
        return {
          background: 'var(--fabric-color-eggplant-alpha-50-alpha-25)',
          color: 'var(--fabric-color-eggplant-70)',
        };
      case 'rose':
        return {
          background: 'var(--fabric-color-rose-alpha-50-alpha-25)',
          color: 'var(--fabric-color-rose-70)',
        };
      case 'red':
        return {
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'tangerine':
        return {
          background: 'var(--fabric-color-tangerine-alpha-50-alpha-25)',
          color: 'var(--fabric-color-tangerine-70)',
        };
      case 'yellow':
        return {
          background: 'var(--fabric-color-yellow-alpha-30-alpha-25)',
          color: 'var(--fabric-color-yellow-70)',
        };
      case 'green':
        return {
          background: 'var(--fabric-color-green-alpha-50-alpha-25)',
          color: 'var(--fabric-color-green-70)',
        };
      case 'seaweed':
        return {
          background: 'var(--fabric-color-seaweed-alpha-50-alpha-25)',
          color: 'var(--fabric-color-seaweed-70)',
        };
      case 'neutral1':
        return {
          background: 'var(--fabric-color-neutral-alpha-100-alpha-10)',
          color: 'var(--fabric-color-neutral-70)',
        };
      case 'neutral2':
        return {
          background: 'var(--fabric-color-neutral-5)',
          color: 'var(--fabric-color-neutral-70)',
        };
      case 'disabled':
        return {
          background: 'var(--fabric-color-neutral-alpha-100-alpha-10)',
          color: 'var(--fabric-color-neutral-50)',
        };

      default:
        return {
          label: '',
          background: 'var(--fabric-color-neutral-00)',
          color: 'var(--fabric-color-neutral-100)',
        };
    }
  }, [variant, t]);

  const displayLabel = children ?? label;
  const displayIcon = icon ?? iconName;
  const hasIconAndLabel = !!displayIcon && !iconOnly;

  return (
    <div
      data-disabled={disabled}
      css={statusBadgeStyles(background, color, hasIconAndLabel, disabled)}
      className={className}
    >
      {displayIcon && <Icon name={displayIcon} size={16} css={{ flexShrink: 0 }} />}
      {!iconOnly && <span>{displayLabel}</span>}
    </div>
  );
};

const statusBadgeStyles = (background: string, color: string, hasIconAndLabel: boolean, disabled: boolean) => css`
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: var(--fabric-space-gap-xxs);
  background: ${disabled ? 'var(--fabric-color-surface-default)' : background};
  color: ${disabled ? 'var(--fabric-color-text-secondary)' : color};
  border: ${disabled ? '1px dashed var(--fabric-color-border-default)' : 'none'};
  padding: ${hasIconAndLabel
    ? 'var(--fabric-space-padding-xxs) var(--fabric-space-padding-xs)'
    : 'calc(var(--fabric-space-padding-xxs) / 2) var(--fabric-space-padding-xxs)'};
  border-radius: var(--fabric-radius-sm);
  font-size: var(--fabric-typography-body-xs-font-size);
`;
